import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ExternalLink,
  Eye,
  EyeOff,
  Plus,
  Trash2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import type { Incident, IncidentStyle, MonitorListItem, StatusPageInput } from "@shared/types";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import * as api from "@/lib/api";
import { queryKeys, useGroups, useMonitors } from "@/lib/queries";
import { cn, formatDateTime } from "@/lib/utils";

type Section = { name: string; monitorIds: number[] };

/** Monitors bucketed by their monitor group, so a whole group can be picked at once. */
type MonitorBucket = { key: string; name: string; monitors: MonitorListItem[] };

const INCIDENT_STYLES: Record<IncidentStyle, string> = {
  info: "border-l-blue-500 bg-blue-500/5",
  warning: "border-l-amber-500 bg-amber-500/5",
  danger: "border-l-status-down bg-status-down/5",
  success: "border-l-status-up bg-status-up/5",
};

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}

function slugify(value: string) {
  return value.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-");
}

export default function StatusPageEditPage() {
  const { id: idParam } = useParams<{ id?: string }>();
  const editing = Boolean(idParam && idParam !== "new");
  const pageId = editing ? Number(idParam) : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: monitors, isLoading: monitorsLoading } = useMonitors();
  const { data: groups } = useGroups();
  const pageQuery = useQuery({
    queryKey: queryKeys.statusPage(pageId),
    queryFn: () => api.getStatusPage(pageId),
    enabled: editing && Number.isInteger(pageId) && pageId > 0,
  });
  const initialized = useRef(false);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [published, setPublished] = useState(true);
  const [showPoweredBy, setShowPoweredBy] = useState(true);
  const [customDomain, setCustomDomain] = useState("");
  const [sections, setSections] = useState<Section[]>([]);
  const [password, setPassword] = useState<string | null | undefined>(undefined);
  const [passwordEditing, setPasswordEditing] = useState(!editing);
  const [saving, setSaving] = useState(false);
  const [deletingPage, setDeletingPage] = useState(false);
  const [incidentTitle, setIncidentTitle] = useState("");
  const [incidentContent, setIncidentContent] = useState("");
  const [incidentStyle, setIncidentStyle] = useState<IncidentStyle>("info");
  const [incidentBusyId, setIncidentBusyId] = useState<number | "new" | null>(null);
  const [deletingIncident, setDeletingIncident] = useState<Incident | null>(null);

  useEffect(() => {
    const page = pageQuery.data;
    if (!page || initialized.current) return;
    initialized.current = true;
    setTitle(page.title);
    setSlug(page.slug);
    setDescription(page.description ?? "");
    setIcon(page.icon ?? "");
    setPublished(page.published);
    setShowPoweredBy(page.showPoweredBy);
    setCustomDomain(page.customDomain ?? "");
    setSections(page.groups.map((group) => ({ name: group.name, monitorIds: [...group.monitorIds] })));
    setPasswordEditing(!page.hasPassword);
  }, [pageQuery.data]);

  const buckets = useMemo<MonitorBucket[]>(() => {
    const list = monitors ?? [];
    const known = new Set((groups ?? []).map((group) => group.id));
    const out: MonitorBucket[] = [];
    for (const group of groups ?? []) {
      const members = list.filter((monitor) => monitor.groupId === group.id);
      if (members.length) out.push({ key: `g-${group.id}`, name: group.name, monitors: members });
    }
    const rest = list.filter((monitor) => monitor.groupId == null || !known.has(monitor.groupId));
    if (rest.length) out.push({ key: "ungrouped", name: "Ungrouped", monitors: rest });
    return out;
  }, [monitors, groups]);

  function changeTitle(value: string) {
    setTitle(value);
    if (!editing && !slugTouched) setSlug(slugify(value));
  }

  function updateSection(index: number, update: (section: Section) => Section) {
    setSections((current) => current.map((section, currentIndex) => currentIndex === index ? update(section) : section));
  }

  function moveSection(index: number, direction: -1 | 1) {
    setSections((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const selected = next[index];
      const other = next[target];
      if (!selected || !other) return current;
      next[index] = other;
      next[target] = selected;
      return next;
    });
  }

  function toggleMonitor(sectionIndex: number, monitorId: number, checked: boolean) {
    updateSection(sectionIndex, (section) => ({
      ...section,
      monitorIds: checked ? [...section.monitorIds, monitorId] : section.monitorIds.filter((id) => id !== monitorId),
    }));
  }

  function toggleBucket(sectionIndex: number, bucket: MonitorBucket, checked: boolean) {
    const ids = bucket.monitors.map((monitor) => monitor.id);
    updateSection(sectionIndex, (section) => ({
      ...section,
      monitorIds: checked
        ? [...section.monitorIds, ...ids.filter((id) => !section.monitorIds.includes(id))]
        : section.monitorIds.filter((id) => !ids.includes(id)),
    }));
  }

  /** Without a bucket this appends an empty section; with one it seeds name + members. */
  function addSection(bucket?: MonitorBucket) {
    setSections((current) => [
      ...current,
      bucket
        ? { name: bucket.name, monitorIds: bucket.monitors.map((monitor) => monitor.id) }
        : { name: "New section", monitorIds: [] },
    ]);
  }

  async function savePage() {
    if (!title.trim() || !slug.trim()) {
      toast.error("Title and slug are required");
      return;
    }
    const baseInput: StatusPageInput = {
      slug: slugify(slug),
      title: title.trim(),
      description: description.trim() || null,
      icon: icon.trim() || null,
      published,
      customDomain: customDomain.trim() || null,
      showPoweredBy,
      groups: sections.map((section) => ({ name: section.name.trim() || "Untitled section", monitorIds: section.monitorIds })),
    };
    const input: StatusPageInput = password === undefined ? baseInput : { ...baseInput, password };
    setSaving(true);
    try {
      if (editing) {
        await api.updateStatusPage(pageId, input);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.statusPage(pageId) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.statusPages }),
        ]);
        setPassword(undefined);
        setPasswordEditing(false);
        toast.success("Status page saved");
      } else {
        const created = await api.createStatusPage(input);
        await queryClient.invalidateQueries({ queryKey: queryKeys.statusPages });
        setPassword(undefined);
        setPasswordEditing(false);
        toast.success("Status page created");
        navigate(`/status-pages/${created.id}`, { replace: true });
      }
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function postIncident() {
    if (!editing || !incidentTitle.trim() || !incidentContent.trim()) return;
    setIncidentBusyId("new");
    try {
      await api.createIncident(pageId, { title: incidentTitle.trim(), content: incidentContent.trim(), style: incidentStyle });
      await queryClient.invalidateQueries({ queryKey: queryKeys.statusPage(pageId) });
      setIncidentTitle("");
      setIncidentContent("");
      setIncidentStyle("info");
      toast.success("Incident posted");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setIncidentBusyId(null);
    }
  }

  async function updateIncident(incident: Incident, update: { pinned?: boolean; resolved?: boolean }, message: string) {
    setIncidentBusyId(incident.id);
    try {
      await api.updateIncident(pageId, incident.id, update);
      await queryClient.invalidateQueries({ queryKey: queryKeys.statusPage(pageId) });
      toast.success(message);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setIncidentBusyId(null);
    }
  }

  async function removeIncident() {
    if (!deletingIncident) return;
    setIncidentBusyId(deletingIncident.id);
    try {
      await api.deleteIncident(pageId, deletingIncident.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.statusPage(pageId) });
      toast.success("Incident deleted");
      setDeletingIncident(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setIncidentBusyId(null);
    }
  }

  async function removePage() {
    setSaving(true);
    try {
      await api.deleteStatusPage(pageId);
      await queryClient.invalidateQueries({ queryKey: queryKeys.statusPages });
      toast.success("Status page deleted");
      navigate("/status-pages");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  if (editing && pageQuery.isLoading) {
    return <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full"><Skeleton className="h-12 w-72" /><div className="grid gap-6 lg:grid-cols-[1fr_380px]"><Skeleton className="h-[700px] rounded-xl" /><Skeleton className="h-[520px] rounded-xl" /></div></div>;
  }

  if (editing && (pageQuery.isError || !Number.isInteger(pageId) || pageId < 1)) {
    return <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full"><PageHeader title="Status page unavailable" description={errorMessage(pageQuery.error)} actions={<Button variant="outline" onClick={() => navigate("/status-pages")}>Back to status pages</Button>} /></div>;
  }

  const page = pageQuery.data;
  const passwordIsSet = page?.hasPassword && password === undefined;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full">
      <PageHeader
        title={editing ? title || page?.title || "Edit status page" : "New status page"}
        actions={<>{editing && <Button variant="outline" asChild><a href={`/status/${slug}`} target="_blank" rel="noreferrer">View page <ExternalLink /></a></Button>}<Button onClick={() => void savePage()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button></>}
      />
      <div className="grid items-start gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Page</CardTitle><CardDescription>Identity and public visibility.</CardDescription></CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2"><Label htmlFor="page-title">Title</Label><Input id="page-title" value={title} onChange={(event) => changeTitle(event.target.value)} placeholder="System status" /></div>
              <div className="space-y-2"><Label htmlFor="page-slug">Slug</Label><div className="flex rounded-md shadow-xs"><span className="flex items-center rounded-l-md border border-r-0 bg-muted px-3 text-sm text-muted-foreground">/status/</span><Input id="page-slug" value={slug} onChange={(event) => { setSlugTouched(true); setSlug(slugify(event.target.value)); }} className="rounded-l-none" placeholder="system-status" /></div></div>
              <div className="space-y-2"><Label htmlFor="page-description">Description</Label><Textarea id="page-description" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Live status for our services" /></div>
              <div className="space-y-2"><Label htmlFor="page-icon">Icon</Label><Input id="page-icon" value={icon} onChange={(event) => setIcon(event.target.value)} placeholder="● or https://example.com/icon.png" /><p className="text-xs text-muted-foreground">Use an emoji or an image URL.</p></div>
              <ToggleRow label="Published" description="Make this page publicly available." checked={published} onCheckedChange={setPublished} />
              <ToggleRow label="Show “Powered by Vigil”" description="Display a small Vigil credit in the footer." checked={showPoweredBy} onCheckedChange={setShowPoweredBy} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Access</CardTitle><CardDescription>Custom domain and password protection.</CardDescription></CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2"><Label htmlFor="custom-domain">Custom domain</Label><Input id="custom-domain" value={customDomain} onChange={(event) => setCustomDomain(event.target.value)} placeholder="status.example.com" /><p className="text-xs leading-relaxed text-muted-foreground">Point this domain at your Vigil server (reverse proxy or A record). The public page will be served at the domain root.</p></div>
              <div className="space-y-2">
                <Label htmlFor="page-password">Password</Label>
                {password === null ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed p-3"><span className="text-sm text-muted-foreground">Password will be removed on save.</span><Button variant="ghost" size="sm" onClick={() => { setPassword(undefined); setPasswordEditing(false); }}>Undo</Button></div>
                ) : passwordIsSet && !passwordEditing ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"><span className="text-sm font-medium">Password is set</span><div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => { setPassword(""); setPasswordEditing(true); }}>Change</Button><Button variant="ghost" size="sm" onClick={() => { setPassword(null); setPasswordEditing(false); }}>Remove</Button></div></div>
                ) : (
                  <div className="flex gap-2"><Input id="page-password" type="password" value={password ?? ""} onChange={(event) => setPassword(event.target.value || undefined)} placeholder={editing ? "Set a new password" : "Optional password"} />{page?.hasPassword && <Button variant="ghost" type="button" onClick={() => { setPassword(undefined); setPasswordEditing(false); }}>Cancel</Button>}</div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4">
              <div><CardTitle>Sections</CardTitle><CardDescription className="mt-1.5">Arrange monitors into groups on the public page.</CardDescription></div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm"><Plus />Add section<ChevronDown /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onSelect={() => addSection()}>Empty section</DropdownMenuItem>
                  {buckets.length > 0 && <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>From monitor group</DropdownMenuLabel>
                    {buckets.map((bucket) => (
                      <DropdownMenuItem key={bucket.key} onSelect={() => addSection(bucket)}>
                        <span className="truncate">{bucket.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground tabular-nums">{bucket.monitors.length}</span>
                      </DropdownMenuItem>
                    ))}
                  </>}
                </DropdownMenuContent>
              </DropdownMenu>
            </CardHeader>
            <CardContent className="space-y-4">
              {!sections.length ? <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">Add a section to show monitors</p> : sections.map((section, index) => (
                <div key={index} className="rounded-xl border p-4">
                  <div className="flex items-center gap-2">
                    <div className="flex shrink-0"><Button size="icon-sm" variant="ghost" aria-label="Move section up" disabled={index === 0} onClick={() => moveSection(index, -1)}><ArrowUp /></Button><Button size="icon-sm" variant="ghost" aria-label="Move section down" disabled={index === sections.length - 1} onClick={() => moveSection(index, 1)}><ArrowDown /></Button></div>
                    <Input aria-label="Section name" value={section.name} onChange={(event) => updateSection(index, (current) => ({ ...current, name: event.target.value }))} />
                    <Button size="icon" variant="ghost" aria-label="Remove section" onClick={() => setSections((current) => current.filter((_, currentIndex) => currentIndex !== index))}><Trash2 /></Button>
                  </div>
                  <div className="mt-4 rounded-lg border">
                    <p className="border-b px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Monitors</p>
                    <ScrollArea className="h-48">
                      <div className="p-2">{monitorsLoading ? <div className="space-y-1">{[0, 1, 2].map((item) => <Skeleton key={item} className="h-8" />)}</div> : buckets.length ? buckets.map((bucket) => {
                        const selected = bucket.monitors.filter((monitor) => section.monitorIds.includes(monitor.id)).length;
                        return (
                          <div key={bucket.key} className="mb-1 last:mb-0">
                            <label className="flex items-center gap-3 rounded-md bg-muted/40 px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-muted/60">
                              <Checkbox aria-label={`Select all in ${bucket.name}`} checked={selected === bucket.monitors.length} onCheckedChange={(checked) => toggleBucket(index, bucket, checked === true)} />
                              <span className="truncate">{bucket.name}</span>
                              <span className="ml-auto tabular-nums">{selected}/{bucket.monitors.length}</span>
                            </label>
                            {bucket.monitors.map((monitor) => (
                              <label key={monitor.id} className="flex items-center gap-3 rounded-md px-2 py-2 pl-7 text-sm hover:bg-muted/50"><Checkbox checked={section.monitorIds.includes(monitor.id)} onCheckedChange={(checked) => toggleMonitor(index, monitor.id, checked === true)} /><span className="truncate">{monitor.name}</span></label>
                            ))}
                          </div>
                        );
                      }) : <p className="p-3 text-center text-sm text-muted-foreground">No monitors available</p>}</div>
                    </ScrollArea>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="space-y-6 lg:sticky lg:top-6">
          <Card>
            <CardHeader><CardTitle>Incidents</CardTitle><CardDescription>{editing ? "Post and manage public incident updates." : "Save the page first to post incidents"}</CardDescription></CardHeader>
            {editing && <CardContent className="space-y-5">
              <div className="space-y-3 rounded-lg border p-3">
                <Input value={incidentTitle} onChange={(event) => setIncidentTitle(event.target.value)} placeholder="Incident title" />
                <Textarea value={incidentContent} onChange={(event) => setIncidentContent(event.target.value)} placeholder="What is happening?" className="min-h-24" />
                <Select value={incidentStyle} onValueChange={(value) => setIncidentStyle(value as IncidentStyle)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="danger">Danger</SelectItem><SelectItem value="success">Success</SelectItem></SelectContent></Select>
                <Button className="w-full" onClick={() => void postIncident()} disabled={incidentBusyId !== null || !incidentTitle.trim() || !incidentContent.trim()}>Post incident</Button>
              </div>
              <div className="space-y-3">
                {!page?.incidents.length ? <p className="py-4 text-center text-sm text-muted-foreground">No incidents posted</p> : page.incidents.map((incident) => (
                  <div key={incident.id} className={cn("rounded-lg border border-l-4 p-3", INCIDENT_STYLES[incident.style], incident.resolvedAt && "opacity-60")}>
                    <div className="flex items-start justify-between gap-2"><h3 className="text-sm font-semibold">{incident.title}</h3>{incident.resolvedAt && <Badge variant="secondary">Resolved</Badge>}</div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{incident.content}</p>
                    <p className="mt-2 text-xs text-muted-foreground">{formatDateTime(incident.createdAt)}</p>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {!incident.resolvedAt && <Button variant="ghost" size="sm" disabled={incidentBusyId !== null} onClick={() => void updateIncident(incident, { resolved: true }, "Incident resolved")}>Resolve</Button>}
                      <Button variant="ghost" size="sm" disabled={incidentBusyId !== null} onClick={() => void updateIncident(incident, { pinned: !incident.pinned }, incident.pinned ? "Incident unpinned" : "Incident pinned")}>{incident.pinned ? <><EyeOff />Unpin</> : <><Eye />Pin</>}</Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={incidentBusyId !== null} onClick={() => setDeletingIncident(incident)}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>}
          </Card>
          {editing && <Card className="border-destructive/40"><CardHeader><CardTitle className="text-destructive">Danger zone</CardTitle><CardDescription>Permanently delete this public page and its incidents.</CardDescription></CardHeader><CardContent><Button variant="destructive" onClick={() => setDeletingPage(true)}>Delete page</Button></CardContent></Card>}
        </div>
      </div>
      <AlertDialog open={deletingPage} onOpenChange={setDeletingPage}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this status page?</AlertDialogTitle><AlertDialogDescription>This permanently removes the public page, sections, and incident history.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => void removePage()} disabled={saving}>Delete page</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
      <AlertDialog open={Boolean(deletingIncident)} onOpenChange={(open) => { if (!open) setDeletingIncident(null); }}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete this incident?</AlertDialogTitle><AlertDialogDescription>This incident update will disappear from the public status page.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => void removeIncident()} disabled={incidentBusyId !== null}>Delete incident</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
}

function ToggleRow({ label, description, checked, onCheckedChange }: { label: string; description: string; checked: boolean; onCheckedChange: (checked: boolean) => void }) {
  return <div className="flex items-center justify-between gap-4 rounded-lg border p-3"><div><Label>{label}</Label><p className="mt-1 text-xs text-muted-foreground">{description}</p></div><Switch checked={checked} onCheckedChange={onCheckedChange} /></div>;
}
